import React, { useState, useEffect } from 'react';
import { businessAPI } from '../../api/businesses';
import { resolveImageUrl } from '../../utils/formatters';

const BusinessProfile = () => {
  const [business, setBusiness] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [geoLoading, setGeoLoading] = useState(false);
  const browserTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  const [formData, setFormData] = useState({
    business_name: '',
    email: '',
    phone: '',
    address: '',
    currency_code: 'USD',
    latitude: '',
    longitude: '',
    timezone: browserTimeZone
  });

  const currencyOptions = [
    { value: 'PKR', label: 'Pakistan Rupees', symbol: '₨' },
    { value: 'USD', label: 'US Dollar', symbol: '$' },
    { value: 'EUR', label: 'Euro', symbol: '€' },
    { value: 'CHF', label: 'Swiss Franc', symbol: 'CHF' },
    { value: 'GBP', label: 'Pound', symbol: '£' }
  ];

  const useMyLocation = async () => {
    if (!navigator?.geolocation) {
      window.alert('Geolocation is not supported in this browser');
      return;
    }

    setGeoLoading(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const lat = pos?.coords?.latitude;
        const lng = pos?.coords?.longitude;
        setFormData((prev) => ({
          ...prev,
          latitude: Number.isFinite(lat) ? String(lat) : '',
          longitude: Number.isFinite(lng) ? String(lng) : ''
        }));
        setGeoLoading(false);
      },
      (err) => {
        window.alert(err?.message || 'Failed to get location');
        setGeoLoading(false);
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
    );
  };

  useEffect(() => {
    loadBusiness();
  }, []);

  const loadBusiness = async () => {
    try {
      const response = await businessAPI.getMyBusiness();
      setBusiness(response.data.data);
      const loaded = response.data.data;
      setFormData({
        business_name: loaded.business_name,
        email: loaded.email || '',
        phone: loaded.phone || '',
        address: loaded.address || '',
        currency_code: loaded.currency_code || 'USD',
        latitude: loaded.latitude !== null && loaded.latitude !== undefined ? String(loaded.latitude) : '',
        longitude: loaded.longitude !== null && loaded.longitude !== undefined ? String(loaded.longitude) : '',
        timezone: loaded.timezone || browserTimeZone
      });
    } catch (error) {
      console.error('Failed to load business:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (business?.pending_change_request) {
        window.alert('You already have pending business changes waiting for admin approval.');
        setSaving(false);
        return;
      }

      const hasLat = String(formData.latitude || '').trim() !== '';
      const hasLng = String(formData.longitude || '').trim() !== '';
      if (hasLat !== hasLng) {
        window.alert('Please provide both latitude and longitude (or leave both empty)');
        setSaving(false);
        return;
      }

      const payload = {
        ...formData,
        latitude: hasLat ? Number(formData.latitude) : undefined,
        longitude: hasLng ? Number(formData.longitude) : undefined
      };

      const res = await businessAPI.update(business.id, payload);
      const data = res?.data?.data;
      if (data?.requires_approval) {
        window.alert('Business changes submitted for admin approval.');
        await loadBusiness();
      } else {
        window.alert('Business profile updated successfully!');
        if (data) setBusiness(data);
      }
    } catch (error) {
      console.error('Failed to update business:', error);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="text-center py-8">Loading...</div>;

  return (
    <div className="max-w-2xl mx-auto">
      <div className="bg-white rounded-lg shadow-md p-6">
        <h1 className="text-2xl font-bold mb-6">Business Profile</h1>

        {business?.pending_change_request && (
          <div className="mb-6 rounded-lg border border-yellow-200 bg-yellow-50 px-4 py-3 text-sm text-yellow-800">
            Pending admin approval for your latest business changes. You can submit a new update after it is reviewed.
          </div>
        )}

        {(business?.settings?.business_profile_image || (business?.settings?.business_images || []).length > 0) && (
          <div className="mb-6 space-y-4">
            {business?.settings?.business_profile_image && (
              <div className="flex items-center gap-4">
                <img
                  src={resolveImageUrl(business.settings.business_profile_image)}
                  alt={business.business_name}
                  className="h-16 w-16 rounded-xl object-cover border"
                />
                <div>
                  <div className="font-semibold text-gray-900">{business.business_name}</div>
                  <div className="text-sm text-gray-500">Business profile image</div>
                </div>
              </div>
            )}

            {(business?.settings?.business_images || []).length > 0 && (
              <div className="grid grid-cols-5 gap-2">
                {(business.settings.business_images || []).slice(0, 5).map((img, idx) => (
                  <img
                    key={idx}
                    src={resolveImageUrl(img)}
                    alt={`${business.business_name} ${idx + 1}`}
                    className="h-16 w-full rounded-lg object-cover border"
                  />
                ))}
              </div>
            )}
          </div>
        )}
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1">Business Name</label>
            <input
              type="text"
              required
              value={formData.business_name}
              onChange={(e) => setFormData({ ...formData, business_name: e.target.value })}
              className="w-full input"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium mb-1">Email</label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              className="w-full input"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium mb-1">Phone</label>
            <input
              type="tel"
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              className="w-full input"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium mb-1">Address</label>
            <textarea
              value={formData.address}
              onChange={(e) => setFormData({ ...formData, address: e.target.value })}
              rows="3"
              className="w-full input"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Currency</label>
            <select
              value={formData.currency_code}
              onChange={(e) => setFormData({ ...formData, currency_code: e.target.value })}
              className="w-full input"
            >
              {currencyOptions.map((c) => (
                <option key={c.value} value={c.value}>
                  {c.label} ({c.symbol})
                </option>
              ))}
            </select>
          </div>

          <div>
            <div className="flex items-center justify-between gap-3">
              <label className="block text-sm font-medium mb-1">Location (Optional)</label>
              <button
                type="button"
                onClick={useMyLocation}
                disabled={geoLoading}
                className="text-sm text-primary-600 hover:text-primary-700 disabled:opacity-60"
              >
                {geoLoading ? 'Getting location...' : 'Use my location'}
              </button>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs text-gray-500 mb-1">Latitude</label>
                <input
                  type="number"
                  inputMode="decimal"
                  step="0.0000001"
                  value={formData.latitude}
                  onChange={(e) => setFormData({ ...formData, latitude: e.target.value })}
                  className="w-full input"
                  placeholder="e.g. 24.8607"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Longitude</label>
                <input
                  type="number"
                  inputMode="decimal"
                  step="0.0000001"
                  value={formData.longitude}
                  onChange={(e) => setFormData({ ...formData, longitude: e.target.value })}
                  className="w-full input"
                  placeholder="e.g. 67.0011"
                />
              </div>
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium mb-1">Timezone</label>
            <input
              type="text"
              value={formData.timezone}
              onChange={(e) => setFormData({ ...formData, timezone: e.target.value })}
              className="w-full input"
              placeholder="e.g. Asia/Karachi, Asia/Kolkata, UTC"
            />
          </div>
          
          <button
            type="submit"
            disabled={saving || !!business?.pending_change_request}
            className="w-full btn-primary disabled:opacity-50"
          >
            {saving ? 'Saving...' : business?.pending_change_request ? 'Pending Admin Approval' : 'Save Changes'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default BusinessProfile;
